// HelpBotCard — chat contra el bot de ayuda (POST /help/ask), incrustado arriba
// del FAQ (mismo patrón que el ForoBot del foro y la página Ayuda de la agenda).
// v1.0 (2026-08-31): historial solo en memoria; mini-parser de **negritas**/listas.
import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, Loader2, MessageCircleWarning } from 'lucide-react';
import { askHelpBot } from '../../../api/helpApi';
import { BaseApiError } from '../../../api/baseClient';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'error';
  text: string;
}

const SUGGESTED_QUESTIONS = [
  '¿Cómo vinculo el WhatsApp de mi negocio?',
  '¿Por qué mi campaña sale tan lento?',
  '¿Cómo registro una venta en el kiosco?',
  '¿Qué le llega a mi cliente por WhatsApp?',
];

const FALLBACK_ERROR_BY_STATUS: Record<number, string> = {
  400: 'Tu pregunta es muy corta — cuéntame un poco más de lo que necesitas.',
  429: 'Se alcanzaron muchas preguntas seguidas. Espera un momento y vuelve a intentar.',
  502: 'El asistente no pudo responder en este momento. Intenta de nuevo en unos segundos.',
};

let nextId = 1;

/** Negritas **texto** dentro de una línea, sin librería de markdown. */
function renderInlineBold(line: string, keyPrefix: string): React.ReactNode[] {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${keyPrefix}-b-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

/** Texto completo -> líneas, con sangría para "1. …" / "- …" / "• …". */
function renderAnswer(text: string): React.ReactNode {
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (trimmed === '') return <div key={idx} className="h-2" />;
    const isListItem = /^(\d+[.)]|[-*•])\s+/.test(trimmed);
    return (
      <p key={idx} className={`leading-relaxed ${isListItem ? 'pl-3' : ''} ${idx > 0 ? 'mt-1' : ''}`}>
        {renderInlineBold(line, String(idx))}
      </p>
    );
  });
}

const HelpBotCard: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (question: string) => {
    const text = question.trim();
    if (!text || sending) return;

    setMessages((prev) => [...prev, { id: nextId++, role: 'user', text }]);
    setInput('');
    setSending(true);

    try {
      const answer = await askHelpBot(text);
      setMessages((prev) => [...prev, { id: nextId++, role: 'assistant', text: answer || 'No tengo una respuesta para eso.' }]);
    } catch (err) {
      const e = err as BaseApiError;
      const isGenericFallback = !e.message || /^HTTP \d+$/.test(e.message);
      const friendly = (isGenericFallback && e.status && FALLBACK_ERROR_BY_STATUS[e.status])
        || e.message
        || 'No se pudo obtener respuesta. Intenta de nuevo en un momento.';
      setMessages((prev) => [...prev, { id: nextId++, role: 'error', text: friendly }]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
        <h2 className="font-medium text-content flex items-center">
          <Sparkles className="h-5 w-5 mr-2 text-accent" />
          Pregúntale al asistente
        </h2>
        <p className="mt-1 text-sm text-content-2">
          Respuestas al momento sobre cómo usar Lealify: kiosco, WhatsApp, campañas, recompensas…
        </p>
      </div>

      {/* Preguntas de ejemplo */}
      <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SUGGESTED_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            disabled={sending}
            onClick={() => send(q)}
            className="shrink-0 whitespace-nowrap rounded-full border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 transition hover:bg-blue-100 dark:hover:bg-blue-500/20 disabled:opacity-40"
          >
            {q}
          </button>
        ))}
      </div>

      {/* Conversación (solo si hay mensajes o está pensando) */}
      {(messages.length > 0 || sending) && (
        <div ref={scrollRef} className="max-h-80 space-y-3 overflow-y-auto px-4 py-3">
          {messages.map((m) => {
            if (m.role === 'user') {
              return (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 px-3.5 py-2 text-sm text-white shadow-sm">
                    {m.text}
                  </div>
                </div>
              );
            }
            if (m.role === 'error') {
              return (
                <div key={m.id} className="flex justify-start">
                  <div className="flex max-w-[85%] items-start gap-2 rounded-2xl rounded-bl-sm bg-amber-50 dark:bg-amber-500/10 px-3.5 py-2 text-sm text-amber-700 dark:text-amber-300 shadow-sm">
                    <MessageCircleWarning size={16} className="mt-0.5 shrink-0" />
                    <span>{m.text}</span>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-surface-2 px-3.5 py-2 text-sm text-content shadow-sm">
                  {renderAnswer(m.text)}
                </div>
              </div>
            );
          })}

          {sending && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-surface-2 px-3.5 py-2 text-sm text-content-3 shadow-sm">
                <Loader2 size={14} className="animate-spin" /> Pensando…
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-center gap-2 border-t border-line p-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu pregunta…"
          disabled={sending}
          className="flex-1 rounded-full border border-line bg-surface-2 px-4 py-2.5 text-sm text-content placeholder:text-content-3 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition hover:bg-blue-700 disabled:opacity-40"
          title="Enviar"
          aria-label="Enviar pregunta"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </form>
    </div>
  );
};

export default HelpBotCard;
